/**
 * Events service — unified data layer for both Alli internal meetings
 * AND Between Sessions community programming. Replaces the old
 * `meetings` table (backfilled in Slice 1, dropped in Slice 4) and
 * the overloaded `supervisions.type='PEER'` community flow.
 *
 * Design doc: /Users/cherry/.claude/plans/linear-jingling-quilt.md
 *
 * Factory pattern (matches meetings.ts / availability.ts) — callers
 * pass their own SupabaseClient (browser, server, or admin) so RLS
 * scoping stays in the caller's hands. All mutating ops assume the
 * caller has service-role scope (typical for API routes that already
 * authed the request).
 */

import { v4 as uuidv4 } from 'uuid'
import type { SupabaseClient } from '@supabase/supabase-js'
import { ConflictError, NotFoundError, ValidationError } from '@repo/errors'
import { materializeSeries } from '../utils/recurrence'

// ---------- Types (mirrors public.* enum values) ----------

export type EventType =
  | 'TEAM_SYNC'
  | 'KNOWLEDGE_NETWORK'
  | 'ONBOARDING'
  | 'LEVELUP_LAB'
  | 'STAT_HOLIDAY'
  | 'ADMIN_MEETING'
  | 'PEER_GROUP'
  | 'BOOK_CLUB'
  | 'SOCIAL'
  | 'CLINICAL_FOCUS'
  | 'WORKSHOP_INTERNAL'
  | 'OTHER'

export type EventAudienceScope = 'ALLI_ONLY' | 'BS_ONLY' | 'BOTH'
export type EventEnrollmentMode = 'OPTIONAL' | 'REQUIRED' | 'INVITE_ONLY'
export type EventStatus = 'SCHEDULED' | 'CANCELLED' | 'COMPLETED'
export type EventAttendeeStatus =
  | 'INVITED'
  | 'REGISTERED'
  | 'WAITLISTED'
  | 'CANCELLED'
  | 'ATTENDED'
  | 'NO_SHOW'
  | 'LATE_CANCEL'
export type EventAttendeeSource = 'AUTO_ENROLLED' | 'RSVP' | 'INVITED' | 'ADMIN_ADDED'
export type PdActivityCategory =
  | 'STUDY_GROUP'
  | 'WORKSHOP'
  | 'DIRECTED_LEARNING'
  | 'CASE_CONSULTATION'
  | 'READING'
  | 'CLINICAL_SUPERVISION'
  | 'OTHER'

/**
 * Structured audience targeting. Undefined fields mean "no filter on
 * this dimension." Empty array means "no matches on this dimension"
 * (which combined with an AND across dimensions typically means the
 * event lands on nobody — that's usually a config bug, not a valid
 * targeting choice, so the create-event UI should refuse to submit
 * empty arrays).
 */
export interface EventAudienceFilter {
  cohorts?: string[]
  employmentStatuses?: string[]
  tiers?: string[]
  roles?: Array<'supervisor' | 'senior_supervisor' | 'admin' | 'employee'>
  proIds?: string[]
}

export interface EventRow {
  id: string
  title: string
  description: string | null
  agenda: string | null
  type: EventType
  category: string | null
  audienceScope: EventAudienceScope
  audienceFilter: EventAudienceFilter | null
  enrollmentMode: EventEnrollmentMode
  maxCapacity: number | null
  waitlistEnabled: boolean
  start: string
  end: string
  timezone: string
  meetingLink: string | null
  location: string | null
  hostProId: string
  additionalHostProIds: string[]
  createdById: string
  seriesId: string | null
  seriesOriginalStart: string | null
  seriesOriginalEnd: string | null
  status: EventStatus
  pdEligible: boolean
  pdCategory: PdActivityCategory | null
  requiresReflection: boolean
  blocksAvailability: boolean
  notifyOnCreate: boolean
  reminderSentAt: string | null
  notes: string | null
  /** Post-event recording link. Null until an admin adds it after the
   *  event happens; then visible to every attendee on their past-
   *  events view. Setting from null → value fires the recording-
   *  available email fanout (see notifyRecordingAvailable). */
  recordingUrl: string | null
  createdAt: string
  updatedAt: string
}

export interface EventAttendeeRow {
  id: string
  eventId: string
  proId: string
  status: EventAttendeeStatus
  waitlistPosition: number | null
  source: EventAttendeeSource
  rsvpAt: string | null
  cancelledAt: string | null
  attendanceMarkedAt: string | null
  cancellationNote: string | null
  reflectionStatus: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REVISION_REQUESTED' | null
  reflectionKeyTakeaways: string | null
  reflectionApplicationAndGrowth: string | null
  reflectionOverallExperience: string | null
  reflectionSubmittedAt: string | null
  reflectionReviewedAt: string | null
  reflectionReviewNote: string | null
  attestedAgreementId: string | null
  attestedAt: string | null
  availabilityId: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Minimal Pro shape needed by authorization helpers. Anything richer
 * than these fields should never leak into event authorization — keeps
 * the surface tight and easy to reason about.
 */
export interface AuthzPro {
  id: string
  isAdmin: boolean | null
  isSupervisor: boolean | null
  isSeniorSupervisor: boolean | null
  isBetweenSession: boolean | null
  employmentStatus: string | null
}

// ---------- Authorization helpers (pure, no DB) ----------

/**
 * Global "can this pro host events at all?" check. Used at the API layer
 * to gate the create endpoint. Mirrors the isAdmin || isSupervisor
 * pattern already used across supervision creation.
 *
 * Note: does NOT check `additionalHostProIds` — that's per-event and
 * belongs to `canHostEvent()`. This is the "your role lets you host in
 * general" answer.
 */
export function canHostEvents(pro: AuthzPro): boolean {
  return (
    !!pro.isAdmin ||
    !!pro.isSupervisor ||
    !!pro.isSeniorSupervisor ||
    pro.employmentStatus === 'EMPLOYEE'
  )
}

/**
 * Per-event host check. Includes the delegated-host allowlist so a BS
 * member added to `additionalHostProIds` can host that specific event
 * even without any global role.
 */
export function canHostEvent(pro: AuthzPro, event: Pick<EventRow, 'hostProId' | 'additionalHostProIds'>): boolean {
  return (
    canHostEvents(pro) ||
    event.hostProId === pro.id ||
    event.additionalHostProIds.includes(pro.id)
  )
}

/**
 * Edit check. Stricter than host: only the primary host or an admin can
 * change event details (time, capacity, cancel, etc.). Delegated hosts
 * can run the event (attendance, notes) but can't rewrite it.
 */
export function canEditEvent(pro: AuthzPro, event: Pick<EventRow, 'hostProId'>): boolean {
  return !!pro.isAdmin || event.hostProId === pro.id
}

/**
 * "Can this pro see this event?" — resolves the audience filter for a
 * list view. Admins bypass. Otherwise the pro must be in-scope (BS
 * member for BS_ONLY, workforce for ALLI_ONLY, anyone for BOTH) AND —
 * only for INVITE_ONLY events — pass the audienceFilter dimensions.
 *
 * Why only INVITE_ONLY: `audienceFilter.proIds` is populated by the
 * event-creation "attendees" picker, which admins used to pre-register
 * people for OPTIONAL/REQUIRED events. That picker inadvertently
 * doubled as a visibility gate, so events like "CBT for Insomnia"
 * (OPTIONAL, audienceScope=BOTH, 122 pre-selected pros) were invisible
 * to every other BS member. Scoping the filter to INVITE_ONLY keeps
 * its useful semantics (truly private events for named attendees) and
 * makes OPTIONAL/REQUIRED events actually open to their audienceScope.
 */
export function canSeeEvent(
  pro: AuthzPro,
  event: Pick<EventRow, 'audienceScope' | 'audienceFilter' | 'enrollmentMode'>,
): boolean {
  if (pro.isAdmin) return true

  // Scope gate
  const inBs = !!pro.isBetweenSession
  const inAlli = !!pro.isSupervisor || !!pro.isSeniorSupervisor || pro.employmentStatus === 'EMPLOYEE'
  if (event.audienceScope === 'BS_ONLY' && !inBs) return false
  if (event.audienceScope === 'ALLI_ONLY' && !inAlli) return false
  // 'BOTH' skips the scope gate — any authed pro is potentially eligible.

  // audienceFilter is ONLY a visibility gate on INVITE_ONLY events.
  // OPTIONAL and REQUIRED events open up to their audienceScope regardless
  // of what's stored in audienceFilter (see fn doc for the rationale).
  if (event.enrollmentMode !== 'INVITE_ONLY') return true

  const f = event.audienceFilter
  if (!f) return true
  if (f.proIds && f.proIds.length > 0 && !f.proIds.includes(pro.id)) return false
  if (f.employmentStatuses && f.employmentStatuses.length > 0) {
    if (!pro.employmentStatus || !f.employmentStatuses.includes(pro.employmentStatus)) return false
  }
  if (f.roles && f.roles.length > 0) {
    const proRoles: string[] = []
    if (pro.isAdmin) proRoles.push('admin')
    if (pro.isSupervisor) proRoles.push('supervisor')
    if (pro.isSeniorSupervisor) proRoles.push('senior_supervisor')
    if (pro.employmentStatus === 'EMPLOYEE') proRoles.push('employee')
    if (!f.roles.some(r => proRoles.includes(r))) return false
  }
  // cohorts + tiers require joining pros → applications; caller can layer
  // that check in when needed (list queries typically filter server-side
  // via SQL rather than iterating this helper).
  return true
}

// ---------- Service factory ----------

export type EventSeriesPattern = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'
export type EventSeriesMonthlyMode = 'DATE_OF_MONTH' | 'NTH_WEEKDAY'
export type EventSeriesStatus = 'ACTIVE' | 'PAUSED' | 'ENDED'

export interface EventSeriesRow {
  id: string
  hostProId: string
  pattern: EventSeriesPattern
  monthlyMode: EventSeriesMonthlyMode | null
  anchorStart: string
  anchorEnd: string
  timezone: string
  occurrenceCount: number
  endsAt: string | null
  status: EventSeriesStatus
  templateJson: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

/** Recurrence spec accepted by `createEvent` to materialize a series. */
export interface EventRecurrenceSpec {
  pattern: EventSeriesPattern
  monthlyMode?: EventSeriesMonthlyMode
  occurrenceCount: number
  endsAt?: string | null
}

export interface CreateEventInput {
  title: string
  description?: string
  agenda?: string
  type: EventType
  category?: string
  audienceScope: EventAudienceScope
  audienceFilter?: EventAudienceFilter | null
  enrollmentMode: EventEnrollmentMode
  maxCapacity?: number | null
  waitlistEnabled?: boolean
  start: string
  end: string
  timezone: string
  meetingLink?: string
  location?: string
  hostProId: string
  additionalHostProIds?: string[]
  createdById: string
  pdEligible?: boolean
  pdCategory?: PdActivityCategory | null
  requiresReflection?: boolean
  blocksAvailability?: boolean
  notifyOnCreate?: boolean
  notes?: string
  /** Recording URL — usually not set at create time but supported for
   *  paste-and-schedule flows (rare). Most values arrive via update. */
  recordingUrl?: string | null
}

export interface UpdateEventInput
  extends Partial<Omit<CreateEventInput, 'createdById' | 'hostProId'>> {
  hostProId?: string
}

/**
 * Ensure every host of an event (primary `hostProId` + each
 * `additionalHostProIds`) has a matching row in `availability` so the
 * event tile shows up on their `/schedule` page. Hosts are NOT attendees
 * (no `event_attendees` row) — the availability write is what puts the
 * event on their calendar. Idempotent: skips proIds that already have
 * a row for this event (attendee-added or host-added).
 *
 * Runs regardless of `event.blocksAvailability` — that flag is an
 * ATTENDEE-facing gate (do OPTIONAL attendees' calendars get blocked
 * or not) and doesn't apply to hosts. A host is always committed to
 * running the event they're hosting, so their calendar block is
 * always warranted. Cancelled events skip since they shouldn't render
 * anywhere on the schedule.
 */
async function syncHostAvailability(client: SupabaseClient, event: EventRow): Promise<void> {
  if (event.status === 'CANCELLED') return
  const hostIds = [event.hostProId, ...(event.additionalHostProIds ?? [])].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  )
  if (hostIds.length === 0) return

  const { data: existing } = await client
    .from('availability')
    .select('proId')
    .eq('eventId', event.id)
    .in('proId', hostIds)
  const covered = new Set((existing || []).map((r: { proId: string }) => r.proId))

  const missing = hostIds.filter(pid => !covered.has(pid))
  if (missing.length === 0) return

  const nowIso = new Date().toISOString()
  const rows = missing.map(pid => ({
    id: uuidv4(),
    proId: pid,
    eventId: event.id,
    start: event.start,
    end: event.end,
    isBlock: true,
    isFollowup: false,
    isPD: event.pdEligible,
    notes: event.title,
    createdAt: nowIso,
    updatedAt: nowIso,
  }))
  const { error } = await client.from('availability').insert(rows)
  if (error) throw error
}

export function createEventsService(client: SupabaseClient) {
  return {
    // ---- Reads ----

    async listEvents(opts?: {
      status?: EventStatus | EventStatus[]
      audienceScope?: EventAudienceScope | EventAudienceScope[]
      fromDate?: string
      toDate?: string
      endBefore?: string
      hostProId?: string
      seriesId?: string
      involvedProId?: string
      limit?: number
      offset?: number
      order?: 'asc' | 'desc'
    }): Promise<EventRow[]> {
      // Pre-fetch attendee eventIds so the events query can express
      // host-or-co-host-or-attendee as a single PostgREST `.or()` filter
      // (no way to join event_attendees inline from PostgREST).
      let attendeeEventIds: string[] | null = null
      if (opts?.involvedProId) {
        const { data: rows, error } = await client
          .from('event_attendees')
          .select('eventId')
          .eq('proId', opts.involvedProId)
        if (error) throw error
        attendeeEventIds = ((rows ?? []) as Array<{ eventId: string }>).map(r => r.eventId)
      }

      let q = client
        .from('events')
        .select('*')
        .order('start', { ascending: opts?.order !== 'desc' })
      if (opts?.status) {
        q = Array.isArray(opts.status) ? q.in('status', opts.status) : q.eq('status', opts.status)
      }
      if (opts?.audienceScope) {
        q = Array.isArray(opts.audienceScope)
          ? q.in('audienceScope', opts.audienceScope)
          : q.eq('audienceScope', opts.audienceScope)
      }
      if (opts?.involvedProId) {
        const pid = opts.involvedProId
        const orParts = [
          `hostProId.eq.${pid}`,
          `additionalHostProIds.cs.{${pid}}`,
        ]
        if (attendeeEventIds && attendeeEventIds.length > 0) {
          orParts.push(`id.in.(${attendeeEventIds.join(',')})`)
        }
        q = q.or(orParts.join(','))
      }
      // `fromDate` filters on `end`, not `start`, so an event that
      // began earlier today but hasn't ended yet still shows up in
      // "upcoming." Filtering on `start` was hiding currently-live
      // events the moment their start time passed — a study group that
      // started 30 min ago and still has 60 min to go would silently
      // disappear from /events, even though members joining late still
      // want to see it.
      if (opts?.fromDate) q = q.gte('end', opts.fromDate)
      if (opts?.toDate) q = q.lte('start', opts.toDate)
      // Gate on `end` (not `start`) so a live event doesn't count as
      // past the moment its start time passes — mirrors the `fromDate`
      // rationale above.
      if (opts?.endBefore) q = q.lt('end', opts.endBefore)
      if (opts?.hostProId) q = q.eq('hostProId', opts.hostProId)
      if (opts?.seriesId) q = q.eq('seriesId', opts.seriesId)
      if (opts?.offset != null || opts?.limit != null) {
        const offset = opts.offset ?? 0
        const limit = opts.limit ?? 50
        q = q.range(offset, offset + limit - 1)
      }
      const { data, error } = await q
      if (error) throw error
      return (data as EventRow[]) || []
    },

    async getEvent(id: string): Promise<EventRow | null> {
      const { data, error } = await client.from('events').select('*').eq('id', id).maybeSingle()
      if (error) throw error
      return (data as EventRow) || null
    },

    async listAttendees(eventId: string): Promise<EventAttendeeRow[]> {
      const { data, error } = await client
        .from('event_attendees')
        .select('*')
        .eq('eventId', eventId)
        .order('createdAt', { ascending: true })
      if (error) throw error
      return (data as EventAttendeeRow[]) || []
    },

    async listMyRsvps(proId: string, opts?: { includePast?: boolean }): Promise<Array<EventAttendeeRow & { event: EventRow }>> {
      let q = client
        .from('event_attendees')
        .select('*, event:events!inner(*)')
        .eq('proId', proId)
        .in('status', ['REGISTERED', 'WAITLISTED', 'INVITED'])
      if (!opts?.includePast) {
        // Filter to attendees whose event hasn't ended yet — same
        // reason as listEvents: gating on `start` drops a member's
        // currently-live RSVP the moment start time passes, even
        // though they still care about it.
        q = q.gte('event.end', new Date().toISOString())
      }
      const { data, error } = await q.order('createdAt', { ascending: false })
      if (error) throw error
      return (data as Array<EventAttendeeRow & { event: EventRow }>) || []
    },

    async countActiveAttendees(eventId: string): Promise<number> {
      const { count, error } = await client
        .from('event_attendees')
        .select('*', { count: 'exact', head: true })
        .eq('eventId', eventId)
        .in('status', ['REGISTERED', 'INVITED'])
      if (error) throw error
      return count || 0
    },

    // ---- Writes ----

    async createEvent(input: CreateEventInput): Promise<EventRow> {
      const id = uuidv4()
      const nowIso = new Date().toISOString()

      // Sensible defaults matching the schema comments.
      const blocksAvailability =
        input.blocksAvailability ??
        (input.enrollmentMode === 'REQUIRED' || input.enrollmentMode === 'OPTIONAL')
      // Fire invitations on-create for both auto-enrolled (REQUIRED) and
      // hand-picked (INVITE_ONLY) events. OPTIONAL events don't invite
      // — members RSVP themselves and get a confirmation instead.
      const notifyOnCreate =
        input.notifyOnCreate ??
        (input.enrollmentMode === 'REQUIRED' || input.enrollmentMode === 'INVITE_ONLY')

      const row = {
        id,
        title: input.title,
        description: input.description ?? null,
        agenda: input.agenda ?? null,
        type: input.type,
        category: input.category ?? null,
        audienceScope: input.audienceScope,
        audienceFilter: input.audienceFilter ?? null,
        enrollmentMode: input.enrollmentMode,
        maxCapacity: input.maxCapacity ?? null,
        waitlistEnabled: input.waitlistEnabled ?? false,
        start: input.start,
        end: input.end,
        timezone: input.timezone,
        meetingLink: input.meetingLink ?? null,
        location: input.location ?? null,
        hostProId: input.hostProId,
        additionalHostProIds: input.additionalHostProIds ?? [],
        createdById: input.createdById,
        status: 'SCHEDULED' as EventStatus,
        pdEligible: input.pdEligible ?? false,
        pdCategory: input.pdCategory ?? null,
        requiresReflection: input.requiresReflection ?? false,
        blocksAvailability,
        notifyOnCreate,
        notes: input.notes ?? null,
        recordingUrl: input.recordingUrl ?? null,
        createdAt: nowIso,
        updatedAt: nowIso,
      }

      const { data, error } = await client.from('events').insert(row).select().single()
      if (error) throw error
      const created = data as EventRow
      // The host isn't an attendee (no event_attendees row) so the
      // schedule-page availability query — which reads from `availability`
      // by proId — would miss them. Write a host availability row here
      // so hostProId + additionalHostProIds see their own event on
      // /schedule from the moment it's created. See syncHostAvailability
      // for the reschedule + host-change edge cases.
      await syncHostAvailability(client, created)
      return created
    },

    /**
     * Create a recurring event series. Writes one `event_series` row +
     * N `events` children via the shared `materializeSeries` expander
     * (same one supervision uses). Every child event carries the
     * `seriesId` FK so admin edits can scope to `this|future|all`.
     *
     * Reuses `createEvent` semantics for each materialized instance so
     * capacity / audience / PD flags stay consistent.
     */
    async createEventSeries(input: {
      base: CreateEventInput
      recurrence: EventRecurrenceSpec
    }): Promise<{ series: EventSeriesRow; events: EventRow[] }> {
      const nowIso = new Date().toISOString()
      const seriesId = uuidv4()

      // Template snapshot: everything a materialized child needs to
      // stand alone without joining the series row.
      const templateJson = {
        title: input.base.title,
        description: input.base.description ?? null,
        agenda: input.base.agenda ?? null,
        type: input.base.type,
        category: input.base.category ?? null,
        audienceScope: input.base.audienceScope,
        audienceFilter: input.base.audienceFilter ?? null,
        enrollmentMode: input.base.enrollmentMode,
        maxCapacity: input.base.maxCapacity ?? null,
        waitlistEnabled: input.base.waitlistEnabled ?? false,
        meetingLink: input.base.meetingLink ?? null,
        location: input.base.location ?? null,
        additionalHostProIds: input.base.additionalHostProIds ?? [],
        pdEligible: input.base.pdEligible ?? false,
        pdCategory: input.base.pdCategory ?? null,
        requiresReflection: input.base.requiresReflection ?? false,
        blocksAvailability: input.base.blocksAvailability ?? true,
        notifyOnCreate: input.base.notifyOnCreate ?? false,
        notes: input.base.notes ?? null,
      }

      const { data: seriesRow, error: seriesErr } = await client
        .from('event_series')
        .insert({
          id: seriesId,
          hostProId: input.base.hostProId,
          pattern: input.recurrence.pattern,
          monthlyMode: input.recurrence.monthlyMode ?? null,
          anchorStart: input.base.start,
          anchorEnd: input.base.end,
          timezone: input.base.timezone,
          occurrenceCount: input.recurrence.occurrenceCount,
          endsAt: input.recurrence.endsAt ?? null,
          status: 'ACTIVE',
          templateJson,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
        .select()
        .single()
      if (seriesErr) throw seriesErr

      const occurrences = materializeSeries({
        pattern: input.recurrence.pattern,
        monthlyMode: input.recurrence.monthlyMode ?? null,
        occurrenceCount: input.recurrence.occurrenceCount,
        anchorStart: new Date(input.base.start),
        anchorEnd: new Date(input.base.end),
        endsAt: input.recurrence.endsAt ? new Date(input.recurrence.endsAt) : null,
      })

      // Create every instance. We call createEvent per row so audience
      // auto-enrollment (REQUIRED + proIds) fires consistently.
      const events: EventRow[] = []
      for (const occ of occurrences) {
        const ev = await this.createEvent({
          ...input.base,
          start: occ.start.toISOString(),
          end: occ.end.toISOString(),
        })
        // Stamp seriesId post-create — createEvent doesn't accept it directly.
        const { data: linked, error: linkErr } = await client
          .from('events')
          .update({ seriesId, updatedAt: new Date().toISOString() })
          .eq('id', ev.id)
          .select()
          .single()
        if (linkErr) throw linkErr
        events.push(linked as EventRow)
      }

      return { series: seriesRow as EventSeriesRow, events }
    },

    async updateEvent(id: string, patch: UpdateEventInput): Promise<EventRow> {
      // Snapshot the pre-edit state so we can diff hosts + timing below.
      // Without this, a reschedule silently leaves attendee availability
      // blocks pointing at the old time, and a host swap orphans the
      // previous host's calendar block.
      const previous = await this.getEvent(id)
      const { data, error } = await client
        .from('events')
        .update({ ...patch, updatedAt: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      const next = data as EventRow

      if (previous) {
        const timingChanged = previous.start !== next.start || previous.end !== next.end
        const titleChanged = previous.title !== next.title
        if (timingChanged || titleChanged) {
          const availPatch: Record<string, unknown> = { updatedAt: new Date().toISOString() }
          if (timingChanged) {
            availPatch.start = next.start
            availPatch.end = next.end
          }
          if (titleChanged) availPatch.notes = next.title
          // Applies to every availability row linked to this event —
          // hosts, auto-enrolled attendees, self-RSVP'd members alike.
          await client.from('availability').update(availPatch).eq('eventId', next.id)
        }
        // Drop rows for any host that got removed. Guard: skip hosts
        // who ALSO have an attendee row for this event (they'd still
        // need their calendar block for the RSVP).
        const prevHosts = new Set(
          [previous.hostProId, ...(previous.additionalHostProIds ?? [])].filter(Boolean),
        )
        const nextHosts = new Set(
          [next.hostProId, ...(next.additionalHostProIds ?? [])].filter(Boolean),
        )
        const removedHosts = [...prevHosts].filter(pid => !nextHosts.has(pid))
        if (removedHosts.length > 0) {
          const { data: stillAttending } = await client
            .from('event_attendees')
            .select('proId')
            .eq('eventId', next.id)
            .in('proId', removedHosts)
            .in('status', ['REGISTERED', 'INVITED', 'WAITLISTED'])
          const keepSet = new Set((stillAttending || []).map((r: { proId: string }) => r.proId))
          const toDelete = removedHosts.filter(pid => !keepSet.has(pid))
          if (toDelete.length > 0) {
            await client
              .from('availability')
              .delete()
              .eq('eventId', next.id)
              .in('proId', toDelete)
          }
        }
      }

      // Add rows for any newly-added hosts (and re-add the primary host
      // if `blocksAvailability` flipped from false → true).
      await syncHostAvailability(client, next)
      return next
    },

    async cancelEvent(id: string): Promise<EventRow> {
      // Cancels the event AND cascades to all REGISTERED/INVITED/WAITLISTED
      // attendees so their availability blocks get cleaned up on the way
      // out. Historically we only dropped rows via `attendee.availabilityId`,
      // but a chunk of the legacy backfill left attendees with the FK
      // unset while the availability row still existed — so blocks got
      // orphaned on schedules after cancel. Belt-and-suspenders: also
      // delete by `availability.eventId` so we sweep every block linked
      // to this event, regardless of attendee FK state.
      await client.from('availability').delete().eq('eventId', id)
      const nowIso = new Date().toISOString()
      await client
        .from('event_attendees')
        .update({ status: 'CANCELLED', cancelledAt: nowIso, availabilityId: null })
        .eq('eventId', id)
        .in('status', ['REGISTERED', 'INVITED', 'WAITLISTED'])
      const { data, error } = await client
        .from('events')
        .update({ status: 'CANCELLED', updatedAt: nowIso })
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data as EventRow
    },

    // ---- RSVP / attendee lifecycle ----

    /**
     * Member RSVPs to an OPTIONAL event, or accepts an INVITED slot.
     * Handles capacity + waitlist automatically. If event.blocksAvailability,
     * writes an availability row so the therapist's bookable calendar
     * reflects the commitment. Idempotent: re-RSVPing on an existing
     * REGISTERED row is a no-op; re-RSVPing on a CANCELLED row reactivates.
     */
    async rsvp(opts: {
      eventId: string
      proId: string
      source?: EventAttendeeSource
    }): Promise<{ attendee: EventAttendeeRow; waitlisted: boolean }> {
      const event = await this.getEvent(opts.eventId)
      if (!event) throw new NotFoundError('Event')
      if (event.status !== 'SCHEDULED') throw new ValidationError('Event is not accepting RSVPs')

      // Existing row check (unique on eventId+proId)
      const { data: existing } = await client
        .from('event_attendees')
        .select('*')
        .eq('eventId', opts.eventId)
        .eq('proId', opts.proId)
        .maybeSingle()

      // Capacity check for REGISTERED slots
      const activeCount = await this.countActiveAttendees(opts.eventId)
      const atCapacity =
        event.maxCapacity !== null && activeCount >= event.maxCapacity

      const wantsWaitlist = atCapacity && event.waitlistEnabled
      if (atCapacity && !event.waitlistEnabled) {
        throw new ConflictError('Event is full')
      }

      const targetStatus: EventAttendeeStatus = wantsWaitlist ? 'WAITLISTED' : 'REGISTERED'
      const source = opts.source ?? 'RSVP'
      const nowIso = new Date().toISOString()

      // Reserve waitlist position when needed (max existing + 1)
      let waitlistPosition: number | null = null
      if (wantsWaitlist) {
        const { data: waitlisted } = await client
          .from('event_attendees')
          .select('waitlistPosition')
          .eq('eventId', opts.eventId)
          .eq('status', 'WAITLISTED')
          .order('waitlistPosition', { ascending: false })
          .limit(1)
        const highest = waitlisted?.[0]?.waitlistPosition ?? 0
        waitlistPosition = highest + 1
      }

      // Write availability block if we're taking a real spot (not waitlist)
      let availabilityId: string | null = existing?.availabilityId ?? null
      if (targetStatus === 'REGISTERED' && event.blocksAvailability && !availabilityId) {
        const availId = uuidv4()
        const { error: availErr } = await client.from('availability').insert({
          id: availId,
          proId: opts.proId,
          eventId: opts.eventId,
          start: event.start,
          end: event.end,
          isBlock: true,
          isFollowup: false,
          isPD: event.pdEligible,
          notes: event.title,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
        if (availErr) throw availErr
        availabilityId = availId
      }

      if (existing) {
        const { data, error } = await client
          .from('event_attendees')
          .update({
            status: targetStatus,
            waitlistPosition,
            source,
            rsvpAt: nowIso,
            cancelledAt: null,
            cancellationNote: null,
            availabilityId,
            updatedAt: nowIso,
          })
          .eq('id', existing.id)
          .select()
          .single()
        if (error) throw error
        return { attendee: data as EventAttendeeRow, waitlisted: targetStatus === 'WAITLISTED' }
      }

      const { data, error } = await client
        .from('event_attendees')
        .insert({
          id: uuidv4(),
          eventId: opts.eventId,
          proId: opts.proId,
          status: targetStatus,
          waitlistPosition,
          source,
          rsvpAt: nowIso,
          availabilityId,
          createdAt: nowIso,
          updatedAt: nowIso,
        })
        .select()
        .single()
      if (error) {
        // If we wrote an availability row before hitting an attendee-insert
        // race (unique conflict), roll it back so we don't leak orphan blocks.
        if (availabilityId) await client.from('availability').delete().eq('id', availabilityId)
        throw error
      }
      return { attendee: data as EventAttendeeRow, waitlisted: targetStatus === 'WAITLISTED' }
    },

    /**
     * Attendee cancels their own RSVP. Drops the availability block and,
     * if the event has a waitlist, promotes the first WAITLISTED row to
     * REGISTERED (which also writes its own availability block).
     */
    async cancelRsvp(opts: {
      eventId: string
      proId: string
      cancellationNote?: string
    }): Promise<{ promoted: EventAttendeeRow | null }> {
      const { data: existing } = await client
        .from('event_attendees')
        .select('*')
        .eq('eventId', opts.eventId)
        .eq('proId', opts.proId)
        .maybeSingle()
      if (!existing) throw new NotFoundError('RSVP to cancel')

      const nowIso = new Date().toISOString()
      const wasRegistered = existing.status === 'REGISTERED'

      // Drop the availability block if any
      if (existing.availabilityId) {
        await client.from('availability').delete().eq('id', existing.availabilityId)
      }
      await client
        .from('event_attendees')
        .update({
          status: 'CANCELLED',
          cancelledAt: nowIso,
          cancellationNote: opts.cancellationNote ?? null,
          availabilityId: null,
          waitlistPosition: null,
          updatedAt: nowIso,
        })
        .eq('id', existing.id)

      // Waitlist promotion — only if the cancelled slot was REGISTERED
      // (waitlist cancels don't free up a real spot).
      if (!wasRegistered) return { promoted: null }

      const { data: nextInLine } = await client
        .from('event_attendees')
        .select('*')
        .eq('eventId', opts.eventId)
        .eq('status', 'WAITLISTED')
        .order('waitlistPosition', { ascending: true })
        .limit(1)
        .maybeSingle()
      if (!nextInLine) return { promoted: null }

      // Promote by re-RSVPing (reuses capacity/availability logic).
      // Drop their waitlist position implicitly via the update path.
      const promoted = await this.rsvp({
        eventId: opts.eventId,
        proId: nextInLine.proId,
        source: nextInLine.source as EventAttendeeSource,
      })
      return { promoted: promoted.attendee }
    },

    /**
     * Admin/host bulk-adds attendees. Used for INVITE_ONLY events and
     * REQUIRED events (auto-enroll from audience). Deduplicates against
     * existing attendee rows.
     */
    async addAttendees(opts: {
      eventId: string
      proIds: string[]
      source: EventAttendeeSource
      status?: EventAttendeeStatus
    }): Promise<EventAttendeeRow[]> {
      if (opts.proIds.length === 0) return []
      const event = await this.getEvent(opts.eventId)
      if (!event) throw new NotFoundError('Event')

      const status = opts.status ?? (opts.source === 'AUTO_ENROLLED' ? 'REGISTERED' : 'INVITED')
      const nowIso = new Date().toISOString()

      // Skip anyone who already has a row for this event.
      const { data: existing } = await client
        .from('event_attendees')
        .select('proId')
        .eq('eventId', opts.eventId)
        .in('proId', opts.proIds)
      const existingSet = new Set((existing || []).map((r: { proId: string }) => r.proId))
      const newProIds = opts.proIds.filter(pid => !existingSet.has(pid))
      if (newProIds.length === 0) return []

      // Write availability blocks first (so if the insert fails partway
      // we don't have attendee rows pointing at nothing). This is a
      // best-effort batch — on partial failure the caller retries.
      //
      // We block for both REGISTERED (auto-enrolled from REQUIRED) AND
      // INVITED (INVITE_ONLY). Rationale: an INVITE_ONLY event created
      // by an admin is an admin-scheduled meeting, not a soft ask —
      // invitees expect to see it on /schedule so they don't
      // double-book, and the schema's blocksAvailability flag is the
      // admin's signal for exactly that. WAITLISTED rows are never
      // blocked (they don't have a confirmed slot).
      const blockableStatuses: EventAttendeeStatus[] = ['REGISTERED', 'INVITED']
      const availRows =
        event.blocksAvailability && blockableStatuses.includes(status)
          ? newProIds.map(pid => ({
              id: uuidv4(),
              proId: pid,
              eventId: opts.eventId,
              start: event.start,
              end: event.end,
              isBlock: true,
              isFollowup: false,
              isPD: event.pdEligible,
              notes: event.title,
              createdAt: nowIso,
              updatedAt: nowIso,
            }))
          : []
      if (availRows.length > 0) {
        const { error: availErr } = await client.from('availability').insert(availRows)
        if (availErr) throw availErr
      }
      const availByPro = new Map(availRows.map(r => [r.proId, r.id]))

      const rows = newProIds.map(pid => ({
        id: uuidv4(),
        eventId: opts.eventId,
        proId: pid,
        status,
        source: opts.source,
        rsvpAt: status === 'REGISTERED' ? nowIso : null,
        availabilityId: availByPro.get(pid) ?? null,
        createdAt: nowIso,
        updatedAt: nowIso,
      }))
      const { data, error } = await client.from('event_attendees').insert(rows).select()
      if (error) {
        // Roll back availability rows on attendee insert failure
        if (availRows.length > 0) {
          await client
            .from('availability')
            .delete()
            .in('id', availRows.map(r => r.id))
        }
        throw error
      }
      return (data as EventAttendeeRow[]) || []
    },

    /**
     * Host marks attendance after the event runs. Attendee-level status
     * write; does NOT touch reflection or PD credit (those live on the
     * attendee row and get set by the reflection flow in Slice 3).
     */
    async markAttendance(opts: {
      eventId: string
      updates: Array<{ proId: string; status: EventAttendeeStatus }>
    }): Promise<void> {
      if (opts.updates.length === 0) return
      const nowIso = new Date().toISOString()
      // No bulk update in PostgREST — issue one per row. Small N in
      // practice (max ~30 for a big group event).
      await Promise.all(
        opts.updates.map(u =>
          client
            .from('event_attendees')
            .update({ status: u.status, attendanceMarkedAt: nowIso, updatedAt: nowIso })
            .eq('eventId', opts.eventId)
            .eq('proId', u.proId),
        ),
      )
    },
  }
}

export type EventsService = ReturnType<typeof createEventsService>
